import { abpParams } from "../adblock/constants";
import { isProxyPath, shouldProxyUpFront, toProxyPath } from "./proxyMode";
import Logger from "./Logger";

/**
 * Convert event object to JSON string
 * Ignores null, undefined or empty values in JSON
 * @param eventObj
 * @returns {*}
 */
export function eventToJsonString(
  eventObj: Record<string, any>
): string | undefined {
  let eventJson: string | undefined;
  try {
    // ignore null, undefined or empty values
    eventJson = JSON.stringify(eventObj, (_, value) => {
      if (value != null && typeof value !== "undefined" && value !== "")
        return value;
      return void 0;
    });
  } catch (e) {
    Logger.error(e);
  }
  Logger.debug("lander event:", eventObj);
  return eventJson;
}

/**
 * Options for {@link postEvent}.
 */
export interface PostEventOptions {
  /**
   * When true, sets `keepalive` on the underlying fetch so the request is not
   * cancelled if the page unloads/navigates immediately after (e.g. a redirect
   * fired in the same tick). Opt-in: existing callers keep the default fetch
   * behavior (keepalive unset). Proxy routing + abpParams are preserved.
   */
  keepalive?: boolean;
}

/**
 * Publish Park Page Event: CLICK, VISIT, etc.
 * Ignores `null` or `undefined` values.
 * NOTE: This function currently does not use async so we can optimize build size for forwarder.
 * @param apiEndpoint API endpoint for POST request
 * @param eventObj Lander event object
 * @param options Optional delivery options (e.g. keepalive for unload-safe sends)
 */
export function postEvent(
  apiEndpoint: string,
  eventObj: Record<string, any>,
  options: PostEventOptions = {}
): Promise<void> | undefined {
  const eventJson = eventToJsonString(eventObj);

  if (!eventJson) return;

  // Route events through the proxy for China traffic (same up-front rule as
  // the config fetch).
  if (shouldProxyUpFront()) apiEndpoint = toProxyPath(apiEndpoint);
  apiEndpoint += "?" + abpParams;

  // Perform POST requst and send event as JSON
  const send = (url: string): Promise<Response> =>
    fetch(url, {
      method: "POST",
      credentials: "omit", // Never send or receive cookies.
      body: eventJson,
      headers: {
        "Content-Type": "application/json",
      },
      // Only set keepalive when opted in; leave fetch defaults untouched otherwise.
      ...(options.keepalive ? { keepalive: true } : {}),
    });

  return send(apiEndpoint)
    .then((response) => {
      if (!response.ok) {
        Logger.error(
          "error publishing event - HTTP Status: " + response.status
        );
      }
    })
    .catch((error) => {
      // A network-level rejection looks like ad-block / firewall blocking.
      // If we weren't already on the proxy, retry the same event via the
      // same-origin /api/ path (mirrors the config-fetch fallback) so blocked
      // sessions' events aren't lost.
      if (!isProxyPath(apiEndpoint)) {
        return send(toProxyPath(apiEndpoint))
          .then((response) => {
            if (!response.ok) {
              Logger.error(
                "error publishing event (proxy) - HTTP Status: " +
                  response.status
              );
            }
          })
          .catch((retryError) => {
            Logger.error(
              "error publishing event (proxy) - " + retryError.toString()
            );
          });
      }
      Logger.error("error publishing event - " + error.toString());
    });
}
